import { ExternalLink } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { formatDateTime, initials } from "@/lib/formatters";
import { sanitizeEmailHtml } from "@/lib/sanitizeHtml";
import type { OutreachBodyType } from "@/api/types";

export type EmailStatus =
  | "sent"
  | "opened"
  | "clicked"
  | "replied"
  | "bounced";

interface EmailDetailSheetProps {
  open: boolean;
  onClose: () => void;
  subject: string | null;
  body: string | null;
  /** "html" → sanitized + rendered as rich content; otherwise plain-text
   *  with whitespace-pre-wrap. Defaults to text when omitted. */
  bodyType?: OutreachBodyType | null;
  /** "outbound" = email we sent (To = contact). "inbound" = reply we
   *  received (From = contact). Drives the title and the people-card
   *  label. */
  direction?: "outbound" | "inbound" | null;
  contactEmail: string | null;
  contactName: string | null;
  sentAt: string | null;
  campaignName: string | null;
  step: number | null;
  status: EmailStatus | null;
  lemlistUrl: string | null;
}

const STATUS_CONFIG: Record<
  EmailStatus,
  {
    label: string;
    emoji: string;
    bg: string;
    text: string;
    border: string;
    bold?: boolean;
  }
> = {
  sent: {
    label: "Sent",
    emoji: "✓",
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
  },
  opened: {
    label: "Opened",
    emoji: "👁",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
  },
  clicked: {
    label: "Clicked",
    emoji: "🖱",
    bg: "bg-violet-50",
    text: "text-violet-700",
    border: "border-violet-200",
  },
  replied: {
    label: "Replied",
    emoji: "💬",
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    border: "border-emerald-200",
    bold: true,
  },
  bounced: {
    label: "Bounced",
    emoji: "❌",
    bg: "bg-rose-50",
    text: "text-rose-700",
    border: "border-rose-200",
  },
};

/**
 * Single source of truth for "show me this email's details" — used by both
 * the global Activity timeline (email_sent / email_sequence_started rows)
 * and the per-company Outreach section's event list. The body field is
 * populated for activity logs that captured it; Lemlist's webhook events
 * don't carry body text, so the component falls back to a "content not
 * available" hint when null.
 */
export function EmailDetailSheet({
  open,
  onClose,
  subject,
  body,
  bodyType,
  direction,
  contactEmail,
  contactName,
  sentAt,
  campaignName,
  step,
  status,
  lemlistUrl,
}: EmailDetailSheetProps) {
  const statusConfig = status ? STATUS_CONFIG[status] : null;
  const isInbound = direction === "inbound";
  const title = isInbound
    ? contactEmail
      ? `Reply from ${contactEmail}`
      : "Reply"
    : step != null
      ? `Email · Step ${step}`
      : "Email sent";
  const personLabel = isInbound ? "From" : "To";
  const displayName = contactName ?? contactEmail ?? "—";

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="flex w-full flex-col gap-0 sm:max-w-[420px]">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className="sr-only">
            Email details, content, and recipient.
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 space-y-4 overflow-y-auto py-4">
          {sentAt && (
            <p className="text-sm font-medium text-slate-700">
              {formatDateTime(sentAt, "MMMM d, yyyy 'at' h:mm a")}
            </p>
          )}

          {statusConfig && (
            <span
              className={cn(
                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                statusConfig.bg,
                statusConfig.text,
                statusConfig.border,
                statusConfig.bold ? "font-bold" : "font-medium",
              )}
            >
              <span>{statusConfig.emoji}</span>
              {statusConfig.label}
            </span>
          )}

          {(contactName || contactEmail) && (
            <div className="space-y-2 rounded-xl bg-slate-50 p-4">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                {personLabel}
              </div>
              <div className="flex items-center gap-3">
                <Avatar className="h-7 w-7 border border-slate-200">
                  <AvatarFallback className="bg-indigo-100 text-[10px] font-semibold text-indigo-700">
                    {initials(displayName)}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  {contactName && (
                    <div className="truncate text-sm font-medium text-slate-900">
                      {contactName}
                    </div>
                  )}
                  {contactEmail && (
                    <div className="truncate font-mono text-xs text-slate-500">
                      {contactEmail}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {subject && (
            <div className="space-y-1.5 rounded-xl bg-slate-50 p-4">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Subject
              </div>
              <p className="text-base font-semibold text-slate-900">
                {subject}
              </p>
            </div>
          )}

          <div className="space-y-2 rounded-xl border border-slate-200 bg-white p-5">
            <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              Content
            </div>
            {body ? (
              bodyType === "html" ? (
                // Sanitized inside an isolated container; tracking pixels,
                // scripts, iframes, and forms are stripped before render.
                <div
                  className="prose prose-sm max-h-[400px] max-w-none overflow-y-auto text-sm leading-relaxed text-slate-700"
                  dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(body) }}
                />
              ) : (
                <p className="max-h-[400px] overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
                  {body}
                </p>
              )
            ) : (
              <div className="space-y-1">
                <p className="text-sm italic text-slate-400">
                  Email content not available
                </p>
                <p className="text-xs text-slate-400">
                  The content was generated by the email sequence in Lemlist
                </p>
              </div>
            )}
          </div>

          {campaignName && (
            <div className="space-y-1.5">
              <div className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Campaign
              </div>
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                {campaignName}
              </span>
            </div>
          )}
        </div>

        <SheetFooter className="flex flex-row gap-2 border-t border-slate-900/[0.06] pt-4 sm:justify-end">
          {lemlistUrl && (
            <Button asChild variant="outline" className="flex-1 sm:flex-none">
              <a
                href={lemlistUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center"
              >
                <ExternalLink className="mr-2 h-3.5 w-3.5" />
                View in Lemlist
              </a>
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            className={lemlistUrl ? "" : "w-full"}
          >
            Close
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
